import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Paginated } from '@/lib/api';
import type { Project, ProjectContact, ProjectContactInput, ProjectInput } from './types';

export const projectKeys = {
  all: ['projects'] as const,
  list: (search: string, status: string) => ['projects', 'list', search, status] as const,
  detail: (id: string) => ['projects', 'detail', id] as const,
};

export function useProjects(search: string, status: string, enabled = true, archived?: string) {
  return useQuery({
    queryKey: [...projectKeys.list(search, status), archived ?? 'hide'],
    queryFn: () =>
      api.paginated<Project>('/projects', {
        query: { search: search || undefined, status: status || undefined, archived, per_page: 100 },
      }),
    // The shell calls this before sign-in; firing it then just earns a 401.
    enabled,
    placeholderData: (previous: Paginated<Project> | undefined) => previous,
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: projectKeys.detail(id),
    queryFn: () => api.get<Project>(`/projects/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ProjectInput) => api.post<Project>('/projects', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: projectKeys.all }),
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ProjectInput }) =>
      api.put<Project>(`/projects/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: projectKeys.all }),
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/projects/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: projectKeys.all }),
  });
}

/** Rotating invalidates every printed poster, so it is a deliberate action. */
export function useRotateQr() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Project>(`/projects/${id}/qr/rotate`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: projectKeys.all }),
  });
}

export function useProjectContacts(projectId: string) {
  return useQuery({
    queryKey: [...projectKeys.detail(projectId), 'contacts'],
    queryFn: () => api.get<ProjectContact[]>(`/projects/${projectId}/contacts`),
    enabled: Boolean(projectId),
  });
}

export function useSaveContact(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: ProjectContactInput }) =>
      id
        ? api.put<ProjectContact>(`/project-contacts/${id}`, body)
        : api.post<ProjectContact>(`/projects/${projectId}/contacts`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: projectKeys.all }),
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/project-contacts/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: projectKeys.all }),
  });
}
